import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject, debounceTime, filter, first, takeUntil } from 'rxjs';
import { ChangePasswordDialogComponent } from 'src/app/dialogs/profile/change-password-dialog/change-password-dialog.component';
import { ChangePasswordSuccessDialogComponent } from 'src/app/dialogs/profile/change-password-success-dialog/change-password-success-dialog.component';
import { DeleteClientAccessDialogComponent } from 'src/app/dialogs/profile/delete-client-access-dialog/delete-client-access-dialog.component';
import {
  ClientsService,
  SelectedClient,
} from 'src/app/services/clients/clients.service';
import { UsersService } from 'src/app/services/users/users.service';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { AddClientDialogComponent } from 'src/app/dialogs/profile/add-client-dialog/add-client-dialog.component';
import { TwoFactorDialogComponent } from 'src/app/dialogs/profile/two-factor-dialog/two-factor-dialog.component';
import { HeaderComponent } from '../header/header.component';
import { MatButton } from '@angular/material/button';
import { PayeeFormFieldComponent } from '../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeInputDirective } from '../../../shared/payee-input/payee-input.directive';
import { MatError } from '@angular/material/input';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { PayeeHorizontalLineComponent } from '../../../shared/payee-horizontal-line/payee-horizontal-line.component';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
  imports: [
    HeaderComponent,
    MatButton,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeInputDirective,
    MatError,
    PayeeButtonComponent,
    PayeeIconComponent,
    PayeeHorizontalLineComponent,
    MatTooltip,
    RouterLink,
    TranslateModule,
  ],
})
export class UserProfileComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private usersService = inject(UsersService);
  private clientsService = inject(ClientsService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private readonly destroy = new Subject<void>();
  private saveCounter = 0;

  readonly userForm = this.fb.group({
    lastName: this.fb.control('', {
      validators: Validators.required,
      nonNullable: true,
    }),
    firstName: this.fb.control('', {
      validators: Validators.required,
      nonNullable: true,
    }),
    email: this.fb.control(
      { value: '', disabled: true },
      { validators: Validators.required, nonNullable: true },
    ),
  });

  get saving() {
    return this.saveCounter > 0;
  }

  canChangePassword = true;
  readonly loading = signal(true);

  has2FA = false;
  userInitials!: string;

  clients: SelectedClient[] = [];

  get lastName() {
    return this.userForm.controls.lastName;
  }
  get firstName() {
    return this.userForm.controls.firstName;
  }
  get email() {
    return this.userForm.controls.email;
  }

  ngOnInit(): void {
    this.canChangePassword = this.authService.origin !== 'PLUGIN_SZAMLAZZHU';

    this.userForm.valueChanges
      .pipe(takeUntil(this.destroy), debounceTime(777))
      .subscribe({
        next: async () => {
          if (!this.userForm.valid) {
            this.userForm.markAsDirty();
            return;
          }

          const { lastName, firstName } = this.userForm.getRawValue();
          this.setUserInitials(lastName, firstName);
          ++this.saveCounter;
          await this.usersService.updateUser({
            name: `${lastName} ${firstName}`,
          });
          setTimeout(() => --this.saveCounter, 2500);
        },
      });

    this.usersService.userSubject
      .pipe(
        takeUntil(this.destroy),
        filter((user) => !!user),
      )
      .subscribe({
        next: (user) => {
          if (!user) return;
          this.has2FA = user.has_2fa ?? false;

          let firstName = '';
          let lastName = '';

          if (!user.name) {
            this.setUserInitials('', '');
          } else {
            [lastName, firstName] = user.name.split(' ', 2);
            this.setUserInitials(lastName, firstName);
          }

          this.userForm.patchValue(
            {
              lastName,
              firstName,
              email: user.email ?? '',
            },
            { emitEvent: false },
          );
        },
      });

    this.clientsService.clientsSubject.pipe(takeUntil(this.destroy)).subscribe({
      next: (clients) => {
        this.loading.set(false);
        this.clients = clients ?? [];
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  changePassword(): void {
    if (!this.canChangePassword) {
      return;
    }

    const dialogRef = this.dialog.open(ChangePasswordDialogComponent, {
      autoFocus: false,
      position: {
        top: '50px',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(first())
      .subscribe(async (result) => {
        if (result) {
          this.dialog.open(ChangePasswordSuccessDialogComponent, {
            autoFocus: false,
            position: {
              top: '50px',
            },
          });
        }
      });
  }

  navigateToClientProfile(client: SelectedClient): void {
    this.clientsService.setSelectedClient(client);
    this.router.navigateByUrl('user/client-profile');
  }

  addClient(): void {
    this.dialog.open(AddClientDialogComponent, {
      autoFocus: false,
    });
  }

  deleteClientAccess(client: SelectedClient): void {
    this.dialog.open(DeleteClientAccessDialogComponent, {
      data: client,
      autoFocus: false,
    });
  }

  manageMFA(): void {
    this.dialog.open(TwoFactorDialogComponent, {
      autoFocus: false,
      data: { has2FA: this.has2FA },
      position: {
        top: '50px',
      },
    });
  }

  private setUserInitials(lastName: string = '', firstName: string = ''): void {
    const lastNameInitial = lastName[0] ?? '';
    const firstNameInitial = firstName[0] ?? '';
    this.userInitials = `${lastNameInitial}${firstNameInitial}`;
  }
}
